<template>
    <div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 sm:p-4 pl-10 sm:pl-4">
        <!-- Left side: Navigation arrows and current date display -->
        <div class="flex min-w-0 items-center space-x-1 sm:space-x-4">
            <!-- Previous button -->
            <button
                @click="navigatePrevious"
                class="shrink-0 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                :disabled="isLoading"
            >
                <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
            </button>

            <!-- Current date display -->
            <div class="min-w-0 text-center">
                <h2 class="truncate text-base sm:text-xl font-semibold text-gray-900 dark:text-white">
                    {{ currentDateDisplay }}
                </h2>
            </div>

            <!-- Next button -->
            <button
                @click="navigateNext"
                class="shrink-0 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                :disabled="isLoading"
            >
                <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
            </button>

            <!-- Today button -->
            <button
                @click="goToToday"
                class="shrink-0 whitespace-nowrap px-3 py-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                :disabled="isLoading"
            >
                {{ $t('schedule.today') }}
            </button>
        </div>

        <!-- Right side: Actions and View selection buttons -->
        <div class="ml-auto flex shrink-0 items-center">
            <!-- Offline Download Button -->
            <button
                @click="onDownloadSchedule"
                class="flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-lg transition-all border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 shadow-sm mr-1 sm:mr-2"
                :title="$t('schedule.downloadOfflineTitle')"
                :disabled="isLoading || isDownloadingSchedule"
            >
                <ArrowPathIcon v-if="isDownloadingSchedule" class="w-4 h-4 text-indigo-600 animate-spin" />
                <CloudArrowDownIcon v-else-if="isScheduleDownloaded" class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <ArrowDownTrayIcon v-else class="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span class="hidden xl:inline text-xs font-semibold">{{ isDownloadingSchedule ? $t('schedule.downloadingOffline') : $t('schedule.downloadOffline') }}</span>
            </button>

            <!-- Desktop View Selection -->
            <div class="hidden lg:flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                    v-for="view in availableViews"
                    :key="view.value"
                    @click="changeView(view.value)"
                    class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
                    :class="currentView === view.value 
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' 
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'"
                    :disabled="isLoading"
                >
                    {{ $t(`schedule.${view.value.toLowerCase()}`) }}
                </button>
            </div>

            <!-- Tour Guide Trigger Button -->
            <button
                type="button"
                @click="emit('start-tour')"
                class="hidden sm:flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm ml-2 cursor-pointer"
                :title="$t('tour.startTour')"
            >
                <QuestionMarkCircleIcon class="w-5 h-5" />
            </button>

            <!-- Mobile Actions Dropdown -->
            <div class="lg:hidden relative ml-1 sm:ml-2 shrink-0">
                <button
                    @click="isMobileActionsOpen = !isMobileActionsOpen"
                    type="button"
                    class="flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm active:scale-95 transform duration-150"
                    title="Quick Actions"
                >
                    <Squares2X2Icon class="w-5 h-5 text-indigo-500" />
                </button>
                
                <!-- Bottom Sheet Menu -->
                <Transition name="bottom-sheet">
                    <div v-if="isMobileActionsOpen" class="fixed inset-0 z-[150] flex flex-col justify-end lg:hidden">
                        <!-- Backdrop -->
                        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300" @click="isMobileActionsOpen = false"></div>
                        
                        <!-- Panel -->
                        <div class="relative bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl p-6 pb-8 max-h-[80vh] overflow-y-auto w-full mx-auto z-10 border-t border-gray-100 dark:border-gray-700">
                            <!-- Handle -->
                            <div class="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-5"></div>
                            
                            <!-- Header -->
                            <div class="flex items-start justify-between mb-6">
                                <div>
                                    <h3 class="text-lg font-bold text-gray-900 dark:text-white">{{ $t('schedule.quickActions') }}</h3>
                                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ $t('schedule.changeView') }}</p>
                                </div>
                                <button @click="isMobileActionsOpen = false" class="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors">
                                    <XMarkIcon class="size-5" />
                                </button>
                            </div>

                            <!-- Buttons grid -->
                            <div class="grid grid-cols-2 gap-4 mb-6">
                                <div 
                                    v-for="view in availableViews"
                                    :key="view.value"
                                    class="flex flex-col items-center text-center cursor-pointer" 
                                    @click="changeView(view.value); isMobileActionsOpen = false"
                                >
                                    <div class="flex items-center justify-center p-4 rounded-2xl transition-all shadow-sm border border-gray-100 dark:border-gray-700 w-full mb-2"
                                         :class="currentView === view.value 
                                            ? 'bg-indigo-50 dark:bg-indigo-900/30 ring-2 ring-indigo-500/50' 
                                            : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-indigo-50 dark:hover:bg-indigo-950/30'">
                                        <span class="text-sm font-medium" 
                                              :class="currentView === view.value ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-700 dark:text-gray-300'">
                                            {{ $t(`schedule.${view.value.toLowerCase()}`) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Event Filters -->
                            <div class="mb-6">
                                <h3 class="text-sm font-bold text-gray-900 dark:text-white mb-3">{{ $t('schedule.eventFilters') }}</h3>
                                <div class="grid grid-cols-2 gap-3">
                                    <div v-for="label in categories" :key="label.name" class="flex items-center">
                                        <div 
                                            class="flex items-center rounded-sm p-0.5" 
                                            :class="getBgColor(label.color, 600)"
                                            :style="getBgStyle(label.color)"
                                        >
                                            <input 
                                                @change="toggleValue(label)" 
                                                type="checkbox" 
                                                :checked="label.selected"
                                                class="w-4 h-4 rounded-sm bg-transparent border-none checked:bg-transparent text-transparent dark:text-transparent focus:ring-0 cursor-pointer"
                                            />
                                        </div>
                                        <label class="ms-2 text-sm font-medium text-gray-700 dark:text-gray-300">{{ label.name }}</label>
                                    </div>
                                </div>
                            </div>

                            <!-- Offline Download Button (Mobile) -->
                            <button 
                                @click="onDownloadSchedule(); isMobileActionsOpen = false"
                                :disabled="isLoading || isDownloadingSchedule"
                                class="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-3 text-gray-700 dark:text-gray-200 font-medium mb-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all active:scale-[0.98]"
                            >
                                <ArrowPathIcon v-if="isDownloadingSchedule" class="w-5 h-5 text-indigo-500 animate-spin" />
                                <CloudArrowDownIcon v-else-if="isScheduleDownloaded" class="w-5 h-5 text-emerald-500" />
                                <ArrowDownTrayIcon v-else class="w-5 h-5 text-indigo-500" />
                                <span>{{ isDownloadingSchedule ? $t('schedule.downloadingOffline') : $t('schedule.downloadOffline') }}</span>
                            </button>

                            <!-- Tour Guide Button (Mobile) -->
                            <button 
                                type="button"
                                @click="emit('start-tour'); isMobileActionsOpen = false"
                                class="w-full flex items-center justify-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-3 text-indigo-600 dark:text-indigo-400 font-medium mb-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all active:scale-[0.98]"
                            >
                                <QuestionMarkCircleIcon class="w-5 h-5 text-indigo-500" />
                                <span>{{ $t('tour.startTour') }}</span>
                            </button>

                            <!-- Add Event Button -->
                            <button 
                                @click="addNewEvent"
                                class="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 p-3 text-white font-medium hover:from-blue-600 hover:to-purple-600 transition-all shadow-md active:scale-[0.98] border-none"
                            >
                                <PlusIcon class="w-5 h-5" />
                                {{ $t('schedule.addEvent') }}
                            </button>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import { 
    Squares2X2Icon, 
    XMarkIcon, 
    PlusIcon, 
    ArrowDownTrayIcon, 
    CloudArrowDownIcon, 
    ArrowPathIcon,
    QuestionMarkCircleIcon
} from '@heroicons/vue/24/outline'
import { getBgColor, getBgStyle } from '@/utilities/color'
import { useI18n } from 'vue-i18n'

// Props
const props = defineProps({
    currentDate: {
        type: Date,
        default: () => new Date()
    },
    currentView: {
        type: String,
        default: 'Month',
        validator: (value) => ['Month', 'Week', 'Day', 'List'].includes(value)
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    categories: {
        type: Array,
        default: () => []
    },
    isDownloadingSchedule: {
        type: Boolean,
        default: false
    },
    isScheduleDownloaded: {
        type: Boolean,
        default: false
    }
})

// Emits
const emit = defineEmits(['date-change', 'view-change', 'add-event', 'filter-change', 'download-schedule', 'start-tour'])

// Reactive data
const { locale } = useI18n()
const currentDate = ref(new Date(props.currentDate))
const currentView = ref(props.currentView)
const isMobileActionsOpen = ref(false)

const onDownloadSchedule = () => {
    emit('download-schedule')
}

// Available views
const availableViews = [
    { label: 'Month', value: 'Month' },
    { label: 'Week', value: 'Week' },
    { label: 'Day', value: 'Day' },
    { label: 'List', value: 'List' }
]

// Computed properties
const currentDateDisplay = computed(() => {
    const date = currentDate.value
    let computedValue = ""
    switch (currentView.value) {
        case 'Month':{
            computedValue = date.toLocaleDateString(locale.value, { 
                month: 'long', 
                year: 'numeric' 
            })
            break;
        }
        case 'Week': {
            const weekStart = getWeekStart(date)
            const weekEnd = getWeekEnd(date)
            
            if (weekStart.getMonth() === weekEnd.getMonth()) {
                computedValue = `${weekStart.toLocaleDateString(locale.value, { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString(locale.value, { day: 'numeric' })}, ${weekEnd.toLocaleDateString(locale.value, { year: 'numeric' })}`
            } else if (weekStart.getFullYear() === weekEnd.getFullYear()) {
                computedValue = `${weekStart.toLocaleDateString(locale.value, { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString(locale.value, { month: 'short', day: 'numeric', year: 'numeric' })}`
            } else {
                computedValue = `${weekStart.toLocaleDateString(locale.value, { month: 'short', day: 'numeric', year: 'numeric' })} - ${weekEnd.toLocaleDateString(locale.value, { month: 'short', day: 'numeric', year: 'numeric' })}`
            }
            break;
        }
        case 'Day':
            {
                computedValue = date.toLocaleDateString(locale.value, { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                })
                break;
            }
        case 'List':{
            computedValue = date.toLocaleDateString(locale.value, { 
                month: 'long', 
                year: 'numeric' 
            })
            break;
        }       
        default: {
            computedValue = date.toLocaleDateString(locale.value, { 
                month: 'long', 
                year: 'numeric' 
            })
            break;
        }
    }
    return computedValue;
})

// Methods
const navigatePrevious = () => {
    const newDate = new Date(currentDate.value)
    
    switch (currentView.value) {
        case 'Month':
            newDate.setMonth(newDate.getMonth() - 1)
            break
        case 'Week':
            newDate.setDate(newDate.getDate() - 7)
            break
        case 'Day':
            newDate.setDate(newDate.getDate() - 1)
            break
        case 'List':
            newDate.setMonth(newDate.getMonth() - 1)
            break
    }
    
    currentDate.value = newDate
    emit('date-change', newDate)
}

const navigateNext = () => {
    const newDate = new Date(currentDate.value)
    
    switch (currentView.value) {
        case 'Month':
            newDate.setMonth(newDate.getMonth() + 1)
            break
        case 'Week':
            newDate.setDate(newDate.getDate() + 7)
            break
        case 'Day':
            newDate.setDate(newDate.getDate() + 1)
            break
        case 'List':
            newDate.setMonth(newDate.getMonth() + 1)
            break
    }
    
    currentDate.value = newDate
    emit('date-change', newDate)
}

const goToToday = () => {
    const today = new Date()
    currentDate.value = today
    emit('date-change', today)
}

const changeView = (view) => {
    currentView.value = view
    emit('view-change', view)
}

const toggleValue = (label) => {
    label.selected = !label.selected
    emit('filter-change', props.categories)
}

const addNewEvent = () => {
    emit('add-event', { date: currentDate.value || new Date(), time: null })
    isMobileActionsOpen.value = false
}

// Helper functions
const getWeekStart = (date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
    return new Date(d.setDate(diff))
}

const getWeekEnd = (date) => {
    const weekStart = getWeekStart(date)
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekStart.getDate() + 6)
    return weekEnd
}

// Watch for prop changes
watch(() => props.currentDate, (newDate) => {
    currentDate.value = new Date(newDate)
})

watch(() => props.currentView, (newView) => {
    currentView.value = newView
})
</script> 